import Vue from 'vue'
import JsonApiObject from '@/_models/JsonApi'
import store from '@/store'
import AttachmentDialog from '@/views/Attachment/dialogs/attachment/attachment.vue'
import AttachmentEditorDialog from '../dialogs/editor/editor.vue'

export default class AttachmentModel extends JsonApiObject {
    public constructor(data: any, included?) {
        super(data, included)

        this.fileId = data.attributes.fileId.toString()
    }

    static async uploadFiles(files: Array<File>) {
        if (files.length > 0) {
            let attachments: Array<any> = []
            await Promise.all(
                Array.from(files).map(async (file) => {
                    let res = await AttachmentModel.postFile(file)
                    attachments.push(res)
                })
            )
            return attachments
        }
        return []
    }

    static retrieveImageFromClipboardAsBlob = (pasteEvent, callback) => {
        if (pasteEvent.clipboardData == false) {
            if (typeof callback == 'function') {
                callback(undefined)
            }
        }

        let items = pasteEvent.clipboardData.items

        if (items == undefined) {
            if (typeof callback == 'function') {
                callback(undefined)
            }
        }

        for (let i = 0; i < items.length; i++) {
            // Skip content if not image
            if (items[i].type.indexOf('image') == -1) continue
            // Retrieve image on clipboard as blob
            let blob = items[i].getAsFile()

            if (typeof callback == 'function') {
                callback(blob)
            }
        }
    }

    fileId
    filename
    size
    canDelete
    createdAt

    get preview() {
        return this.fileId
            ? `https://attachment.${process.env.VUE_APP_VBIT_API}/files/${this.fileId}/preview`
            : null
    }

    get url() {
        return this.fileId
            ? `https://attachment.${process.env.VUE_APP_VBIT_API}/files/${this.fileId}`
            : null
    }

    get type() {
        let nameSplit = this.filename.split('.')
        return nameSplit.pop().toLowerCase()
    }

    async download() {
        if (this.url) window.open(this.url)
    }

    async showLog(attachment, functions?) {
        return await JsonApiObject.openDialog(AttachmentDialog, {
            attachment: attachment,
            functions,
        })
    }

    async delete() {
        let query = {
            updatedAt: this['updatedAt'],
        }
        return await Vue.prototype.$vbit.request('delete', this._links.self, null, query)
    }

    static preview(fileId: string) {
        return fileId
            ? `https://attachment.${process.env.VUE_APP_VBIT_API}/files/${fileId}/preview`
            : null
    }

    static url(fileId: string) {
        return fileId ? `https://attachment.${process.env.VUE_APP_VBIT_API}/files/${fileId}` : null
    }

    static async download(document: any) {
        let url = AttachmentModel.url(document.fileId)
        if (url) window.open(url)
    }

    static async postFile(fileBlob) {
        let fileId: null | string = null
        let nameSplit = fileBlob.name.split('.')
        let type = nameSplit.pop().toLowerCase()
        let name = encodeURIComponent(nameSplit.join('.'))
            .replace(/['()]/g, escape)
            .replace(/\*/g, '%2A')
            .replace(/%(?:7C|60|5E)/g, unescape)
        let nameFull = `${name}.${type}`
        let size = fileBlob.size
        let query = {
            contentType: fileBlob.type,
            contentDisposition: `attachment; filename*=UTF-8''${nameFull}; size=${fileBlob.size}`,
        }

        // Trial upload
        let res = await Vue.prototype.$vbit.postAttachment(query).catch((error) => {
            error.data.response.data.errors.map((error: any) => {
                if (error.detail == "The header 'Content-Disposition' contained an invalid value") {
                    store.dispatch('errorSnackbar', 'Wrong file name')
                } else {
                    store.dispatch('errorSnackbar', 'Problems with uploading the file')
                }
            })
        })

        if (res && res.status == '200') {
            fileId = res.data.fileId
        }

        // No existing files upload
        if (res && res.status == '204') {
            query = {
                contentType: fileBlob.type,
                contentDisposition: `attachment; filename*=UTF-8''${nameFull}`,
            }

            let file = await Vue.prototype.$vbit.postAttachment(query, fileBlob).catch((error) => {
                store.dispatch('errorSnackbar', 'Problems with uploading the file')
            })
            fileId = file.data.fileId
            if (file.data.size) {
                size = file.data.size
            }
        }

        let file = {
            id: fileId,
            filename: nameFull,
            size: size,
            ...(fileId && { preview: this.preview(fileId) }),
            type: type,
        }
        return file
    }

    static async post(module: string, fileId, rel) {
        let query = {
            body: {
                data: {
                    type: 'attachment',
                    attributes: {
                        fileId: fileId,
                    },
                    relationships: {
                        [rel._type]: {
                            data: {
                                id: rel._id,
                                type: rel._type,
                            },
                        },
                    },
                },
            },
        }

        let res = await Vue.prototype.$vbit.post(module, 'attachments', query)

        return new AttachmentModel(res.data, res.included)
    }

    static async initEdit(fileObject, uploadingAttachment) {
        return await JsonApiObject.openDialog(AttachmentEditorDialog, {
            fileObject: fileObject,
            uploadingAttachment: uploadingAttachment,
        })
    }
}
